import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';

import {
  getSalesDemoMode,
  reloadProjectWorkspace,
  salesDemoModeInitialized,
} from '../../features/sales-demo/salesDemoMode';
import { keycloak } from '../../keycloak';
import { env } from './env';

const icsApi = axios.create({ baseURL: env.VITE_ICS_API_URL });
const retried = new WeakSet<InternalAxiosRequestConfig>();

const inIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

icsApi.interceptors.request.use((config) => {
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token');
  const token = urlToken || keycloak.token;

  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (salesDemoModeInitialized())
    config.headers['X-Sales-Demo-Mode'] = getSalesDemoMode() ? 'sales' : 'production';
  return config;
});

icsApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ error: string; details: string }>) => {
    const { response, config } = error;
    if (response?.status === 409 && response.headers['x-sales-demo-mode'] === 'changed') {
      reloadProjectWorkspace();
      return Promise.reject(error);
    }

    if (error.response?.data?.details) {
      error.message = error.response.data.details;
    }

    if (!response || !config) {
      return Promise.reject(error);
    }

    if (response.status === 401 && keycloak.authenticated && !retried.has(config)) {
      try {
        await keycloak.updateToken(30);

        retried.add(config);
        const retryConfig: InternalAxiosRequestConfig = { ...config };
        if (keycloak.token) {
          retryConfig.headers.Authorization = `Bearer ${keycloak.token}`;
        }
        return icsApi(retryConfig);
      } catch {
        if (inIframe) {
          keycloak.authenticated = false;
          window.dispatchEvent(new CustomEvent('keycloak-token-expired'));
        } else {
          keycloak.login();
        }
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export { icsApi };
